import React from 'react'
import {
  WImage,
  Button,
  EditableTitle,
  EditableDescription,
  Tag,
  WContainer,
} from '@components/atoms'
import styles from './TextImageLarge.module.sass'
import { getIcon } from '@utils/icons'
import { checkUrlAndTrackPageBuilder } from '@utils/index'
import { useRouter } from 'next/router'
import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y, Navigation, Pagination } from 'swiper/modules'
import { MenuItemType } from '@utils/types'

interface Props {
  item: MenuItemType
  images?: string[]
  data?: string
  caption?: string
  imagePos?: 'left' | 'right'
  theme?: 'light' | 'neutrallight' | 'secondarydark' | 'black' | 'primary'
  variant?: 'default' | 'carousel-contained'
  editMode?: boolean
  staticMedia?: boolean
  className?: string
  nextImage?: boolean
  volantini?: boolean
}

const TextImageLarge = (props: Props) => {
  const {
    item = undefined,
    images = [],
    data = undefined,
    caption = undefined,
    imagePos = 'left',
    theme = 'light',
    variant = 'default',
    editMode = false,
    staticMedia = false,
    className = '',
    nextImage = false,
    volantini = false,
  } = props

  const router = useRouter()
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const element = contentRef.current
    if (!element) return

    const updateHeight = () => {
      const newHeight = element.offsetHeight
      setContentHeight((prev) => (prev !== newHeight ? newHeight : prev))
    }

    let resizeObserver: ResizeObserver | null = null
    let resizeListenerAttached = false

    updateHeight()

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeight)
      resizeObserver.observe(element)
    } else {
      window.addEventListener('resize', updateHeight)
      resizeListenerAttached = true
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      if (resizeListenerAttached) {
        window.removeEventListener('resize', updateHeight)
      }
    }
  }, [item, caption, data])

  const imageBoxStyle = React.useMemo<React.CSSProperties | undefined>(() => {
    if (!contentHeight || contentHeight <= 0) return undefined
    return { '--text-block-height': `${contentHeight}px` } as React.CSSProperties
  }, [contentHeight])

  const isContainedLayout = variant === 'carousel-contained'

  const componentContent = (
    <>
      {images.length <= 1 || variant === 'default' ? (
        <div
          className={`${styles.imageBox} ${volantini ? styles['imageBox--volantini'] : ''}`}
          style={imageBoxStyle}
        >
          {!!item.image && (
            <WImage
              src={item.image}
              maxWidth={960}
              maxHeight={500}
              objectFit={volantini ? 'contain' : 'cover'}
              alt={item.alt}
              title={item.alt}
              staticMedia={staticMedia}
              next={nextImage}
            />
          )}
        </div>
      ) : (
        <div className={styles.slider}>
          <Swiper
            slidesPerView={1.3}
            spaceBetween={20}
            modules={[A11y, Navigation, Pagination]}
            centerInsufficientSlides
            pagination={{
              type: 'progressbar',
            }}
            breakpoints={{
              576: { slidesPerView: 1 },
            }}
            navigation
            loop
            className={'swiper--bottomNav swiper--no-nav-bg'}
          >
            {images?.map((y, z) => (
              <SwiperSlide key={z}>
                <div className={styles.imageBoxCarousel}>
                  <WImage
                    src={y}
                    maxWidth={648}
                    maxHeight={486}
                    objectFit="cover"
                    alt={item.alt}
                    title={item.alt}
                    staticMedia={staticMedia}
                    next={nextImage}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
      <div>
        <div className={styles.main} ref={contentRef}>
          {(!!item.name || !!item.description) && (
            <div className={styles.content}>
              <Tag
                label={item.intro}
                variant={['secondarydark', 'black'].includes(theme) ? 'white' : 'black'}
                size={'sm'}
                className={styles.intro}
                editMode={editMode}
              />
              <EditableTitle
                title={data}
                variant="span"
                className={styles.data}
                editMode={editMode}
              />
              <EditableTitle title={item.name} className={styles.title} editMode={editMode} />
              <EditableDescription
                description={item.description}
                className={styles.description}
                editMode={editMode}
              />
              <EditableDescription
                description={caption}
                className={styles.caption}
                editMode={editMode}
              />
            </div>
          )}
          {editMode ? (
            <>{item.children}</>
          ) : (
            !!item.children?.filter((x) => !!x.url && !!x.ctaLabel)?.length && (
              <div className={styles.ctaBox}>
                {item.children?.map((x) => (
                  <Button
                    key={x.pk}
                    label={x.ctaLabel}
                    href={x.url}
                    onClick={() => checkUrlAndTrackPageBuilder(x, router.asPath, item.name || '')}
                    newPage={x.blank}
                    iconLeft={
                      x.iconLeft
                        ? x.iconLeft
                        : !!x.icon && x.iconPos === 'left'
                        ? getIcon(x.icon)
                        : undefined
                    }
                    iconRight={
                      x.iconRight
                        ? x.iconRight
                        : !!x.icon && x.iconPos === 'right'
                        ? getIcon(x.icon)
                        : undefined
                    }
                    variant={x.style}
                    color={
                      ['secondary', 'ghost'].includes(x.style) &&
                      ['secondarydark', 'black'].includes(theme)
                        ? 'white'
                        : ''
                    }
                    theme={['secondarydark', 'black'].includes(theme) ? 'dark' : 'light'}
                    size={'lg'}
                    mobileSize={'md'}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </>
  )

  return item ? (
    <div
      className={`${styles.root} ${styles[`root--${theme}`]} ${
        styles[`root--imagePos-${imagePos}`]
      } 
      ${item.borderTop ? styles[`root--bt`] : ''} 
      ${item.borderBottom ? styles[`root--bb`] : ''} 
      ${editMode ? styles['root--editMode'] : ''} 
      ${isContainedLayout ? styles['root--contained'] : ''} ${className}`}
    >
      {isContainedLayout ? <WContainer>{componentContent}</WContainer> : componentContent}
    </div>
  ) : (
    <></>
  )
}

export default TextImageLarge
